/**
 * Cliente del API.
 *
 * Los tipos de las rutas de negocio NO se escriben a mano ni se comparten por
 * un paquete npm: el backend genera OpenAPI desde los esquemas Zod del
 * catalogo de herramientas (seccion 6) y `pnpm api:types` genera este cliente
 * tipado desde ahi. Asi los dos repositorios no pueden divergir.
 *
 * Todo lo que sale hacia el API pasa por `send`: el JSON por `request`, y las
 * subidas multipart llamando a `send` directamente. Es el unico sitio que pone
 * la base y las cabeceras de autenticacion.
 */
import { HTTP, readApiError } from "./api-error.ts";
import { API_BASE, apiUrl } from "./api-routes.ts";

export { ApiError } from "./api-error.ts";

export interface Health {
  readonly status: string;
  readonly version: string;
}

/**
 * Lo que el cliente necesita de la sesion, sin saber como funciona.
 *
 * Lo inyecta `auth.ts` al arrancar, en vez de importarlo aqui, para no crear
 * un ciclo entre los dos modulos.
 */
export interface AuthHooks {
  /** Las cabeceras de autenticacion de ESTE momento. */
  readonly headers: () => Record<string, string>;
  /**
   * Renueva el access token. `true` si hay uno nuevo. Tiene que ser
   * single-flight: diez peticiones con 401 a la vez son UN refresh.
   */
  readonly refresh: () => Promise<boolean>;
  /** No hubo forma de recuperar la sesion. */
  readonly lost: () => void;
}

let auth: AuthHooks = {
  headers: () => ({}),
  refresh: () => Promise.resolve(false),
  lost: () => undefined,
};

export const setAuthHooks = (hooks: AuthHooks): void => {
  auth = hooks;
};

/**
 * Las rutas que NO se reintentan tras refrescar: son las que dan o renuevan
 * la sesion, y un 401 suyo refrescando seria un bucle.
 */
const WITHOUT_REFRESH = [`${API_BASE}/auth/`];

const attempt = (url: string, init: RequestInit): Promise<Response> => {
  const headers = new Headers(init.headers);
  for (const [key, value] of Object.entries(auth.headers())) {
    if (!headers.has(key)) headers.set(key, value);
  }
  return fetch(url, { ...init, headers });
};

/**
 * EL `fetch` del cliente: pone la autenticacion y, ante un 401, renueva la
 * sesion y reintenta UNA vez.
 *
 * Es el unico sitio donde pasa, y por el pasa todo: `request`, las subidas,
 * el dictado y el stream del chat. El cuerpo se puede reenviar porque aqui
 * solo viajan textos y `FormData`, nunca un stream.
 *
 * Si el refresh falla, o el reintento vuelve a dar 401, la sesion se da por
 * perdida y se devuelve la respuesta tal cual: quien llamo ve su 401 y la
 * sesion decide que hacer (relanzar el SSO).
 */
const authorized = async (
  url: string,
  init: RequestInit,
): Promise<Response> => {
  const response = await attempt(url, init);
  if (
    response.status !== HTTP.unauthorized ||
    WITHOUT_REFRESH.some((path) => url.includes(path))
  ) {
    return response;
  }

  if (!(await auth.refresh())) {
    auth.lost();
    return response;
  }

  const retried = await attempt(url, init);
  if (retried.status === HTTP.unauthorized) auth.lost();
  return retried;
};

/**
 * Manda una peticion al API y devuelve la respuesta tal cual, sin mirarla.
 *
 * Es para quien necesita el `Response` --el dictado distingue sus fallos por
 * estado--. Lo normal es `request`, que ademas abre el sobre.
 */
export const send = (path: string, init: RequestInit = {}): Promise<Response> =>
  authorized(apiUrl(path), init);

/**
 * El `fetch` que se le da al transporte del chat (`DefaultChatTransport`).
 *
 * Sin el, el SDK convierte un fallo en un `Error` con el cuerpo crudo como
 * mensaje --el JSON entero en pantalla--. Con el, llega un `ApiError` como
 * del resto del cliente, y `errorMessage` sabe que decir.
 */
export const chatFetch = async (
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> => {
  const url =
    typeof input === "string"
      ? input
      : input instanceof URL
        ? input.href
        : input.url;
  const response = await authorized(url, init ?? {});
  if (!response.ok) throw await readApiError(response);
  return response;
};

export const request = async <T>(
  path: string,
  init: RequestInit = {},
): Promise<T> => {
  const headers = new Headers(init.headers);
  // Un cuerpo multipart NO lleva Content-Type puesto a mano: el navegador
  // escribe el suyo con el boundary, y pisarlo rompe la subida.
  if (!headers.has("Content-Type") && !(init.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }

  const response = await send(path, { ...init, headers });

  if (!response.ok) throw await readApiError(response);

  // Un 204 no lleva cuerpo, asi que pedirle JSON reventaria justo cuando todo
  // ha ido bien. Lo devuelve el borrado: no hay nada que contar de algo que ya
  // no existe.
  if (response.status === NO_CONTENT) return undefined as T;

  return unwrap<T>(await response.json());
};

/** Respondio bien y no hay cuerpo que leer. */
const NO_CONTENT = 204;

/**
 * Saca el contenido del sobre `{ data: ... }` (seccion 6.1).
 *
 * Se hace AQUI y no en cada llamada para que el sobre sea cosa del transporte
 * y no se filtre a los tipos de todo el cliente: quien llama pide
 * `ConversationSummary[]`, no `{ data: ConversationSummary[] }`.
 *
 * Se comprueba que el sobre este, en vez de darlo por hecho, porque las
 * sondas (`/health`, `/ready`) no lo llevan: no son API.
 */
const unwrap = <T>(body: unknown): T =>
  typeof body === "object" && body !== null && "data" in body
    ? (body as { data: T }).data
    : (body as T);

export const getHealth = (): Promise<Health> => request<Health>("/health");
